/**
 * @file group-slider.component.tsx
 * @description Компонент выдвижной панели управления группами в каталоге Task Manager приложения. Предоставляет плавающую кнопку для отображения слайдера с иерархическим списком групп через Tree компонент, модальное окно для создания новых групп через ObjectForm и анимации появления/скрытия через react-transition-group. Обеспечивает обнаружение кликов вне области компонента для автоматического закрытия. Используется для фильтрации каталогов по группам с возможностью создания новых групп.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-19
 * @dependencies ['react', '@components/ui/base/button', '@consta/icons/IconForward', '@consta/icons/IconAdd', '@consta/icons/IconFolders', '@components/ui/data/tree', 'react-transition-group', 'csstype', 'react-detect-click-outside', '@features/object-form', '@utils/form-object-helper']
 * @related-files ['src/features/catalog/catalog.component.tsx', 'src/components/ui/data/tree/tree.component.tsx', 'src/features/object-form', 'src/utils/form-object-helper.ts']
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@components/ui/base/button';
import { IconForward } from '@consta/icons/IconForward';
import { IconAdd } from '@consta/icons/IconAdd';
import { IconFolders } from '@consta/icons/IconFolders';
import { Tree } from '@components/ui/data/tree';
import { Transition, TransitionStatus } from 'react-transition-group';
import CSS from 'csstype';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { ObjectForm } from '@features/object-form';
import { getObjectEntity } from '@utils/form-object-helper';
import { IBasePage } from '@src/types/page';
import type { IGroupSliderProps } from '@src/types/tree-components';

export const GroupSlider = ({
    groups,
    onCheckItem,
    type,
    updateDataHandler = null,
    right = '10.4684vmin',
    bottom = '4.2342vmin'
}: IGroupSliderProps) => {
    const [isShow, setIsShow] = useState<boolean>(false);
    const [isShowAddGroupModal, setIsShowAddGroupModal] = useState<boolean>(false);
    const isCanCloseSliderRef = useRef<boolean>(false);
    const ref = useDetectClickOutside({ onTriggered: () => (isCanCloseSliderRef.current ? setIsShow(false) : null) });
    const groupEntity = useMemo<IBasePage | null>(() => getObjectEntity(type), [type]);

    useEffect(() => {
        setTimeout(() => (isCanCloseSliderRef.current = isShow), 300);
    }, [isShow]);

    const transitionStyles: Partial<Record<TransitionStatus, CSS.Properties>> = {
        entering: { right: '-36.19044vmin', transition: `right ${300}ms ease-out` },
        entered: { right: 0, transition: `right ${200}ms ease-in 100ms` },
        exiting: { right: 0, transition: `right ${300}ms ease-out` },
        exited: { right: '-36.19044vmin', transition: `right ${300}ms ease-out` }
    };

    const defaultStyle: Partial<CSS.Properties> = {
        position: 'absolute',
        right: '-36.19044vmin',
        height: '-webkit-fill-available',
        backgroundColor: '#F1F5F6',
        zIndex: 100,
        marginTop: '-2.1164vmin',
        width: '36.19044vmin',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 1.2698vmin 2.96296vmin 0 #0020331F'
    };

    return (
        <div>
            <Button
                size="l"
                form="round"
                onClick={() => setIsShow(true)}
                iconLeft={IconFolders}
                className="add-row-button transition-transform fixed rounded-full"
                style={{ right, bottom }}
            />

            <div>
                <Transition nodeRef={ref} in={isShow} exit={!isShow} timeout={0}>
                    {(state) => (
                        <div
                            style={{
                                ...defaultStyle,
                                ...transitionStyles[state]
                            }}
                            ref={ref}
                        >
                            <div className="py-[2.1164vmin] px-[1.0582vmin]">
                                <Tree
                                    data={groups}
                                    onCheckItems={(newCheckedItems: string[]) =>
                                        onCheckItem && onCheckItem(newCheckedItems)
                                    }
                                    groupIcons={true}
                                    checkbox={true}
                                />
                            </div>

                            <div>
                                <Button
                                    size="l"
                                    form="round"
                                    onClick={() => setIsShow(false)}
                                    iconLeft={IconForward}
                                    className="add-row-button transition-transform absolute rounded-full"
                                    style={{ right, bottom }}
                                />

                                <Button
                                    size="l"
                                    form="round"
                                    onClick={() => setIsShowAddGroupModal(true)}
                                    iconLeft={IconAdd}
                                    className="add-row-button transition-transform absolute rounded-full"
                                    style={{ right: bottom, bottom }}
                                />
                            </div>
                        </div>
                    )}
                </Transition>
            </div>

            {isShowAddGroupModal && groupEntity && (
                <ObjectForm
                    type={type}
                    onClose={() => {
                        updateDataHandler && updateDataHandler();
                        setIsShowAddGroupModal(false);
                    }}
                    {...groupEntity}
                />
            )}
        </div>
    );
};
